<template>
  <div>
    <v-row class="mb-2 mx-2 align-center">
      <v-text-field class="pa-2" v-model="search" append-icon="mdi-magnify"
                    :label="$t('general.misc.search')" clearable hide-details></v-text-field>
      <v-chip :color="!filterOnArchived ? 'dark-gray' : archivedFilter ? 'primary' : 'info'"
              text-color="white" class="mt-3 ml-2 mr-2"
              @contextmenu="$event.preventDefault(); archivedFilter = !archivedFilter"
              @click="filterOnArchived = !filterOnArchived">
        <v-avatar left>
          <v-icon>{{ archivedFilter ? 'mdi-archive-plus' : 'mdi-archive-minus' }}</v-icon>
        </v-avatar>
        <span v-if="archivedFilter">{{ $t('management.admin.screenshots.archived') }}</span>
        <span v-else>{{ $t('management.admin.screenshots.not_archived') }}</span>
      </v-chip>
      <v-spacer></v-spacer>
      <v-btn @click="openReorderDialog" color="secondary" class="mr-2 mt-2">
        <v-icon>mdi-format-list-numbered</v-icon>
        <span class="ml-1">{{ $t('management.admin.screenshots.reorder') }}</span>
      </v-btn>
      <v-btn @click="isOpenCreateDialog = true" color="primary" class="mr-1 mt-2">
        <v-icon>mdi-plus-circle</v-icon>
        <span class="ml-1">{{ $t('general.actions.create') }}</span>
      </v-btn>
    </v-row>
    <v-data-table :headers="headers" :items="screenshots" item-key="id"
                  :search="search" :loading="isLoading"
                  :page.sync="page" :items-per-page="itemsPerPage" hide-default-footer
                  @page-count="pageCount = $event" :custom-filter="Utilities().searchNormalized">
      <template v-slot:item.description="{ item }">
        <v-row class="pa-2 align-center" dense v-if="item.edit">
          <v-text-field v-model="item.description"></v-text-field>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn @click="updateDescription(item)" icon class="mb-1" color="primary" v-on="on">
                <v-icon>mdi-content-save</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('management.admin.screenshots.save_description') }}</span>
          </v-tooltip>
        </v-row>
        <v-row class="pa-2 align-center" dense v-else>
          <span>{{ item.description }}</span>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn @click="item.edit = !item.edit" icon class="mb-1" color="primary" v-on="on">
                <v-icon>mdi-pencil-circle</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('management.admin.screenshots.update_description') }}</span>
          </v-tooltip>
        </v-row>
      </template>
      <template v-slot:item.orderNumber="{ item }">
        <span>{{ item.orderNumber ?? "-" }}</span>
      </template>
      <template v-slot:item.isArchived="{ item }">
        <v-chip small :color="item.isArchived ? 'primary' : 'info'" text-color="white" class="mr-1 mb-1">
          <v-avatar left>
            <v-icon small>{{ item.isArchived ? 'mdi-archive-plus' : 'mdi-archive-minus' }}</v-icon>
          </v-avatar>
          <span v-if="item.isArchived">{{ $t('management.admin.screenshots.archived') }}</span>
          <span v-else>{{ $t('management.admin.screenshots.not_archived') }}</span>
        </v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn @click="preview = item.content; isOpenPreviewDialog = true;" icon color="primary" v-on="on">
              <v-icon>mdi-image</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('management.admin.screenshots.preview') }}</span>
        </v-tooltip>

        <v-tooltip top v-if="!item.isArchived">
          <template v-slot:activator="{ on }">
            <v-btn @click="updateArchiveState(item, true)" icon color="primary" v-on="on">
              <v-icon>mdi-archive-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('management.admin.screenshots.archive') }}</span>
        </v-tooltip>
        <v-tooltip top v-if="item.isArchived">
          <template v-slot:activator="{ on }">
            <v-btn @click="updateArchiveState(item, false)" icon color="primary" v-on="on">
              <v-icon>mdi-archive-minus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('management.admin.screenshots.activate') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn @click="deleteScreenshot(item)" icon color="primary" v-on="on">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('management.admin.screenshots.remove') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination v-if="screenshots.length > 0" v-model="page" :length="pageCount"></v-pagination>
    </div>

    <v-dialog v-model="isOpenCreateDialog" max-width="400px">
      <v-card>
        <v-toolbar flat dense color="orange darken-3">
          <v-icon class="mr-1">mdi-image</v-icon>
          {{ $t('management.admin.screenshots.create') }}
        </v-toolbar>
        <v-card-text class="pa-3">
          <v-text-field :label="$t('management.admin.screenshots.table.description')"
                        v-model="dialogModel.screenshotDescription" class="mr-2"/>
          <v-alert dense text type="info" class="mt-2 mb-0">
            {{ $t('management.admin.screenshots.latest_order_hint', { order: nextActiveOrderNumber }) }}
          </v-alert>
          <v-file-input :label="$t('management.admin.screenshots.table.image')"
                        v-model="dialogModel.screenshot" accept="image/png, image/jpeg"
                        class="mr-2"></v-file-input>
        </v-card-text>
        <v-card-actions class="pa-3 justify-end">
          <v-btn class="mr-2" @click="isOpenCreateDialog = false">
            {{ $t('general.actions.cancel') }}
          </v-btn>
          <v-btn @click="uploadScreenshot()"
                 :disabled="dialogModel.screenshot === null || dialogModel.screenshotDescription === ''"
                 color="primary">
            {{ $t('management.admin.screenshots.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isOpenReorderDialog" max-width="700px">
      <v-card>
        <v-toolbar flat dense color="orange darken-3">
          <v-icon class="mr-1">mdi-format-list-numbered</v-icon>
          {{ $t('management.admin.screenshots.reorder') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="isOpenReorderDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-3">
          <p class="mb-3 grey--text text--lighten-1">{{ $t('management.admin.screenshots.reorder_hint') }}</p>
          <div class="reorder-list-wrap">
            <draggable
              v-model="reorderItems"
              handle=".drag-handle"
              :scroll="true"
              :scroll-sensitivity="120"
              :scroll-speed="14"
              :bubble-scroll="true"
            >
              <div
                v-for="(item, idx) in reorderItems"
                :key="item.id"
                class="d-flex align-center pa-2 mb-2 reorder-entry"
              >
                <v-icon class="drag-handle mr-2">mdi-drag</v-icon>
                <span class="mr-3 grey--text">#{{ idx + 1 }}</span>
                <span>{{ item.description }}</span>
              </div>
            </draggable>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3 justify-end">
          <v-btn class="mr-2" @click="isOpenReorderDialog = false">
            {{ $t('general.actions.cancel') }}
          </v-btn>
          <v-btn color="primary" :loading="isSavingReorder" @click="saveReorder">
            {{ $t('general.actions.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isOpenPreviewDialog" max-width="1200px">
      <v-card class="mx-auto">
        <v-card-text class="pa-0">
          <div class="preview-shell">
            <div class="preview-controls">
              <v-btn icon class="preview-control-btn" @click="isOpenPreviewDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="preview-image-wrap">
              <img v-if="preview" :src="preview" class="preview-image" alt="Screenshot preview">
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import DataService from "@/services/data.service";
import MessageService from "@/services/message.service";
import Utilities from "@/util/Utilities";

export default {
  name: "ScreenshotManagement",
  components: {draggable},
  data() {
    return {
      search: "",
      isLoading: true,
      page: 1,
      pageCount: 0,
      itemsPerPage: 100,
      filterOnArchived: false,
      archivedFilter: false,
      isOpenCreateDialog: false,
      isOpenPreviewDialog: false,
      isOpenReorderDialog: false,
      isSavingReorder: false,
      preview: null,
      reorderItems: [],
      dialogModel: {
        screenshot: null,
        screenshotDescription: ""
      },
      screenshots: [],
      headers: [
        {text: this.$t('management.admin.screenshots.table.order_number'), value: "orderNumber", sortable: true},
        {text: this.$t('management.admin.screenshots.table.description'), value: "description"},
        {
          text: this.$t('management.admin.screenshots.archived'),
          value: "isArchived",
          filter: value => {
            return !this.filterOnArchived || value === this.archivedFilter;
          }
        },
        {text: this.$t('general.timestamps.created_at'), value: "createdAt"},
        {text: this.$t('general.timestamps.updated_at'), value: "updatedAt"},
        {text: this.$t('general.misc.actions'), value: "actions"}
      ]
    }
  },
  async mounted() {
    if (!this.currentUser?.rights.includes("READ_ADMIN_PANEL") && this.$route.path !== '/home') {
      await this.$router.push('/home');
      return;
    }
    await this.loadScreenshots();
  },
  methods: {
    Utilities() {
      return Utilities;
    },
    normalizeScreenshots(images) {
      return images.map(image => ({
        ...image,
        edit: false,
        updatedAtRaw: image.updatedAt,
        createdAt: `${new Date(image["createdAt"]).toLocaleDateString()} ${new Date(image["createdAt"]).toLocaleTimeString()}`,
        updatedAt: `${new Date(image["updatedAt"]).toLocaleDateString()} ${new Date(image["updatedAt"]).toLocaleTimeString()}`,
      })).sort((a, b) => {
        if (a.isArchived !== b.isArchived) {
          return a.isArchived ? 1 : -1;
        }
        if (!a.isArchived) {
          return (a.orderNumber ?? 0) - (b.orderNumber ?? 0);
        }
        return new Date(b.updatedAtRaw).getTime() - new Date(a.updatedAtRaw).getTime();
      });
    },
    async loadScreenshots() {
      this.isLoading = true;
      const images = await DataService.getImages(false);
      this.screenshots = this.normalizeScreenshots(images);
      this.isLoading = false;
    },
    uploadScreenshot() {
      const reader = new FileReader();
      reader.readAsDataURL(this.dialogModel.screenshot);
      reader.onload = (() => {
        DataService.createImage(this.dialogModel.screenshotDescription, reader.result).then(data => {
          if (data) {
            this.isOpenCreateDialog = false;
            this.dialogModel.screenshot = null;
            this.dialogModel.screenshotDescription = "";
            MessageService.success("Screenshot successfully uploaded.");
            this.loadScreenshots();
          }
        });
      });
      reader.onerror = (() => {
        MessageService.error("Could not upload screenshot!");
      });
    },
    updateArchiveState(screenshot, isArchived) {
      DataService.updateImage(screenshot.id, {
        isArchived: isArchived
      }).then(success => {
        if (success) {
          MessageService.success("Screenshot successfully updated.");
          this.loadScreenshots();
        }
      });
    },
    updateDescription(screenshot) {
      DataService.updateImage(screenshot.id, {
        description: screenshot.description
      }).then(success => {
        if (success) {
          screenshot.edit = false;
          MessageService.success("Screenshot successfully updated.");
          this.loadScreenshots();
        }
      });
    },
    openReorderDialog() {
      this.reorderItems = this.screenshots
          .filter(screenshot => !screenshot.isArchived)
          .sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0))
          .map(screenshot => ({ id: screenshot.id, description: screenshot.description }));
      this.isOpenReorderDialog = true;
    },
    saveReorder() {
      const imageIds = this.reorderItems.map(item => item.id);
      this.isSavingReorder = true;
      DataService.reorderImages(imageIds).then(success => {
        if (success) {
          MessageService.success("Screenshots successfully reordered.");
          this.isOpenReorderDialog = false;
          this.loadScreenshots();
        }
      }).finally(() => {
        this.isSavingReorder = false;
      });
    },
    deleteScreenshot(screenshot) {
      DataService.deleteImage(screenshot.id).then(success => {
        if (success) {
          MessageService.success("Screenshot successfully deleted.");
          const index = this.screenshots.findIndex(x => x.id === screenshot.id);
          if (index !== -1) {
            this.screenshots.splice(index, 1);
            this.$forceUpdate();
          }
        }
      });
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/user"];
    },
    nextActiveOrderNumber() {
      const activeScreenshots = this.screenshots.filter(screenshot => !screenshot.isArchived);
      if (activeScreenshots.length === 0) return 1;
      const latestOrder = activeScreenshots.reduce((latest, screenshot) => Math.max(latest, screenshot.orderNumber || 0), 0);
      return latestOrder + 1;
    }
  }
}
</script>

<style scoped>
.reorder-list-wrap {
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 6px;
}

.reorder-entry {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
}

.drag-handle {
  cursor: grab;
}

.preview-shell {
  position: relative;
}

.preview-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
}

.preview-control-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(18, 18, 18, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.55);
}

.preview-image-wrap {
  max-height: 70vh;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #121212;
  padding: 8px;
}

.preview-image {
  max-width: 100%;
  max-height: calc(70vh - 16px);
  object-fit: contain;
}
</style>
